import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { Refresh } from '@mui/icons-material';

const LOADING_MESSAGES = [
  'Crunching the numbers...',
  'Consulting the prediction markets...',
  'Analyzing probabilities...',
  'Running simulations...',
  'Calculating expected values...',
  'Calibrating risk models...',
  'Scanning event contracts...',
  'Building your thesis...',
];

const DAD_JOKES = [
  "Why did the trader bring a ladder? Because the market was looking up!",
  "I told my portfolio a joke. It didn't get any returns.",
  "What's a bear market's favorite music? Heavy metal... because everything drops.",
  "Why don't scientists trust atoms? Because they make up everything, just like market analysts.",
  "I tried to catch some fog today. I mist. Kind of like my last trade.",
  "What do you call a sleeping dinosaur? A dino-snore. Also, my portfolio performance.",
  "Why did the spreadsheet go to therapy? Too many broken cells.",
  "I'm reading a book about anti-gravity. It's impossible to put down, unlike my stocks.",
  "What's the difference between a portfolio manager and a pizza? A pizza can feed a family of four.",
  "Why did the algorithm go to school? To improve its learning rate!",
];

interface LoadingStateProps {
  message?: string;
}

export const LoadingSpinner: React.FC<LoadingStateProps> = ({ message }) => {
  const [msgIndex, setMsgIndex] = useState(() => Math.floor(Math.random() * LOADING_MESSAGES.length));

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        gap: 2,
      }}
    >
      <CircularProgress size={40} sx={{ color: '#3A8C8C' }} />
      <Typography
        variant="body2"
        sx={{
          color: '#7A5F4E',
          fontStyle: 'italic',
          textAlign: 'center',
          transition: 'opacity 0.3s ease',
        }}
      >
        {message || LOADING_MESSAGES[msgIndex]}
      </Typography>
    </Box>
  );
};

interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ message, onRetry }) => {
  const [joke] = useState(() => DAD_JOKES[Math.floor(Math.random() * DAD_JOKES.length)]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        gap: 2,
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" sx={{ color: '#C0392B' }}>
        Something went wrong
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: '#7A5F4E', maxWidth: 400 }}
      >
        {message || 'Failed to load data. Please try again.'}
      </Typography>
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: 'rgba(58,140,140,0.08)',
          border: '1px solid rgba(58,140,140,0.2)',
          maxWidth: 400,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: '#7A5F4E', fontStyle: 'italic', fontSize: '0.85rem' }}
        >
          "{joke}"
        </Typography>
      </Box>
      {onRetry && (
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={onRetry}
          sx={{ mt: 1 }}
        >
          Try Again
        </Button>
      )}
    </Box>
  );
};

export default LoadingSpinner;
